import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { CircleAlert, FileText, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import {
  attachmentDocumentCategories,
  attachmentDocumentCategoryLabels,
  parseAttachmentKeywords,
  type AttachmentDocumentCategory,
  type AttachmentDocumentGateway,
  type AttachmentDocumentRecord,
} from '@forq/platform-data';

interface AttachmentDocumentsFeatureProps {
  readonly tenant: TenantSummary;
  readonly gateway: AttachmentDocumentGateway;
  readonly canEdit: boolean;
}

const MAX_BYTES = 8 * 1024 * 1024;

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Die Dokumente konnten nicht geladen werden.';
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Die Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });
}

function ruleSummary(document: AttachmentDocumentRecord): string {
  if (document.alwaysOffer) return 'immer anbieten';
  if (document.keywords.length > 0) return `bei: ${document.keywords.join(', ')}`;
  return 'nur manuell';
}

interface DraftState {
  readonly fileName: string;
  readonly mimeType: string;
  readonly byteSize: number;
  readonly content: string;
  title: string;
  category: AttachmentDocumentCategory;
  alwaysOffer: boolean;
  keywordsText: string;
}

interface EditState {
  readonly id: string;
  title: string;
  category: AttachmentDocumentCategory;
  alwaysOffer: boolean;
  keywordsText: string;
}

/**
 * Verwaltung der Zusatzdokumente/Prüflisten je Mandant: eigene PDF-Vordrucke hochladen, benennen,
 * kategorisieren und die Vorschlag-Regel (immer / bei Stichwort im Vorgangstext) setzen. Diese
 * Dokumente werden beim Druck eines Werkstattauftrags zum Mitdrucken angeboten.
 */
export function AttachmentDocumentsFeature({ tenant, gateway, canEdit }: AttachmentDocumentsFeatureProps) {
  const [documents, setDocuments] = useState<readonly AttachmentDocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDocuments(await gateway.listDocuments(tenant.id));
    } catch (loadError) {
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  const pickFile = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    if (file.size > MAX_BYTES) { setError('Die Datei ist größer als 8 MB.'); return; }
    try {
      const content = await readFileAsDataUrl(file);
      setDraft({
        fileName: file.name,
        mimeType: file.type || 'application/pdf',
        byteSize: file.size,
        content,
        title: file.name.replace(/\.[^.]+$/, ''),
        category: 'checklist',
        alwaysOffer: false,
        keywordsText: '',
      });
    } catch (readError) {
      setError(messageOf(readError));
    }
  }, []);

  const addDraft = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      await gateway.addDocument({
        tenantId: tenant.id,
        title: draft.title,
        category: draft.category,
        mimeType: draft.mimeType,
        byteSize: draft.byteSize,
        alwaysOffer: draft.alwaysOffer,
        keywords: parseAttachmentKeywords(draft.keywordsText),
        content: draft.content,
      });
      setDraft(null);
      await load();
    } catch (addError) {
      setError(messageOf(addError));
    } finally {
      setBusy(false);
    }
  }, [draft, gateway, tenant.id, load]);

  const saveEdit = useCallback(async () => {
    if (!edit) return;
    setBusy(true);
    setError(null);
    try {
      await gateway.updateDocument({
        tenantId: tenant.id,
        documentId: edit.id,
        title: edit.title,
        category: edit.category,
        alwaysOffer: edit.alwaysOffer,
        keywords: parseAttachmentKeywords(edit.keywordsText),
      });
      setEdit(null);
      await load();
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setBusy(false);
    }
  }, [edit, gateway, tenant.id, load]);

  const remove = useCallback(async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await gateway.removeDocument(tenant.id, id);
      await load();
    } catch (removeError) {
      setError(messageOf(removeError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id, load]);

  const openDocument = useCallback(async (id: string) => {
    setError(null);
    try {
      const full = await gateway.getDocument(tenant.id, id);
      const win = window.open('', '_blank');
      if (win) {
        win.document.title = full.title;
        win.document.body.style.margin = '0';
        win.document.body.innerHTML = `<iframe src="${full.content}" title="${full.title}" style="border:0;width:100vw;height:100vh"></iframe>`;
      }
    } catch (openError) {
      setError(messageOf(openError));
    }
  }, [gateway, tenant.id]);

  return (
    <section className="admin-docs">
      <header className="admin-docs__head">
        <div>
          <h2>Prüflisten &amp; Zusatzdokumente</h2>
          <p>Eigene Vordrucke (PDF), die beim Druck eines Werkstattauftrags zum Mitdrucken angeboten werden — z.&nbsp;B. UVV-Prüfung, Maschinenankunftsprotokoll, Elektro-Prüfung.</p>
        </div>
        {canEdit && (
          <>
            <input ref={fileInputRef} type="file" accept="application/pdf,image/*" hidden onChange={(event) => void pickFile(event)} />
            <button type="button" className="admin-docs__upload" onClick={() => fileInputRef.current?.click()}>
              <Upload size={15} /> PDF hochladen
            </button>
          </>
        )}
      </header>

      {error && <div className="admin-docs__error"><CircleAlert size={15} /> {error}</div>}

      {draft && (
        <div className="admin-docs__form">
          <div className="admin-docs__form-file"><FileText size={15} /> {draft.fileName} · {Math.round(draft.byteSize / 1024)} KB</div>
          <label>Name<input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /></label>
          <label>Kategorie
            <select value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value as AttachmentDocumentCategory })}>
              {attachmentDocumentCategories.map((category) => <option key={category} value={category}>{attachmentDocumentCategoryLabels[category]}</option>)}
            </select>
          </label>
          <label className="admin-docs__check">
            <input type="checkbox" checked={draft.alwaysOffer} onChange={(event) => setDraft({ ...draft, alwaysOffer: event.target.checked })} />
            Immer beim Werkstattauftrag anbieten
          </label>
          <label>Stichwörter (Vorschlag, wenn im Vorgangstext)
            <input value={draft.keywordsText} placeholder="UVV, Durchsicht, Elektro" disabled={draft.alwaysOffer}
              onChange={(event) => setDraft({ ...draft, keywordsText: event.target.value })} />
          </label>
          <div className="admin-docs__form-actions">
            <button type="button" className="is-secondary" onClick={() => setDraft(null)}>Abbrechen</button>
            <button type="button" disabled={busy || !draft.title.trim()} onClick={() => void addDraft()}><Plus size={15} /> Hinzufügen</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="admin-docs__loading">Dokumente werden geladen …</div>
      ) : documents.length === 0 ? (
        <div className="admin-docs__empty">Noch keine Zusatzdokumente hinterlegt.{canEdit ? ' Lade oben ein PDF hoch.' : ''}</div>
      ) : (
        <ul className="admin-docs__list">
          {documents.map((document) => edit && edit.id === document.id ? (
            <li key={document.id} className="admin-docs__row admin-docs__row--edit">
              <label>Name<input value={edit.title} onChange={(event) => setEdit({ ...edit, title: event.target.value })} /></label>
              <label>Kategorie
                <select value={edit.category} onChange={(event) => setEdit({ ...edit, category: event.target.value as AttachmentDocumentCategory })}>
                  {attachmentDocumentCategories.map((category) => <option key={category} value={category}>{attachmentDocumentCategoryLabels[category]}</option>)}
                </select>
              </label>
              <label className="admin-docs__check">
                <input type="checkbox" checked={edit.alwaysOffer} onChange={(event) => setEdit({ ...edit, alwaysOffer: event.target.checked })} />
                Immer beim Werkstattauftrag anbieten
              </label>
              <label>Stichwörter
                <input value={edit.keywordsText} placeholder="UVV, Durchsicht, Elektro" disabled={edit.alwaysOffer}
                  onChange={(event) => setEdit({ ...edit, keywordsText: event.target.value })} />
              </label>
              <div className="admin-docs__form-actions">
                <button type="button" className="is-secondary" onClick={() => setEdit(null)}>Abbrechen</button>
                <button type="button" disabled={busy || !edit.title.trim()} onClick={() => void saveEdit()}>Speichern</button>
              </div>
            </li>
          ) : (
            <li key={document.id} className="admin-docs__row">
              <FileText size={18} className="admin-docs__row-icon" />
              <div className="admin-docs__row-meta">
                <span className="admin-docs__row-title">{document.title}</span>
                <span className="admin-docs__row-sub">{attachmentDocumentCategoryLabels[document.category]} · {ruleSummary(document)} · {Math.round(document.byteSize / 1024)} KB</span>
              </div>
              <div className="admin-docs__row-actions">
                <button type="button" onClick={() => void openDocument(document.id)}>Öffnen</button>
                {canEdit && (
                  <button type="button" title="Bearbeiten" onClick={() => setEdit({ id: document.id, title: document.title, category: document.category, alwaysOffer: document.alwaysOffer, keywordsText: document.keywords.join(', ') })}>
                    <Pencil size={14} />
                  </button>
                )}
                {canEdit && (
                  <button type="button" className="is-danger" title="Entfernen" disabled={busy} onClick={() => void remove(document.id)}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
